/**
 * ConcentrationDistributionChart — box-and-whisker per well.
 * Native hand-rolled SVG (no Recharts). Categorical x (well names);
 * useChartView is omitted because rectangle-zoom on a band scale doesn't
 * have meaningful semantics. Y-axis regulatory MCL lines render with the
 * dv-mcl-glow filter.
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { scaleLinear, scaleBand } from 'd3-scale';
import type { AnalyticalTimeseriesResponse, ConcentrationUnit } from '@/api/dataVault';
import { WellSelectorPanel } from './WellSelectorPanel';
import { WELL_COLORS } from './chartTheme';
import { ChartTooltip } from './ChartTooltip';
import { useRegulatoryLines } from '../hooks/useRegulatoryLines';
import { defaultHiddenWellIds, defaultSelectedAnalyteCode } from './chartDefaults';

interface Props {
  data: AnalyticalTimeseriesResponse;
  unit: ConcentrationUnit;
}

function convertConc(val: number | null, _unit: ConcentrationUnit): number | null {
  if (val === null) return null;
  return val;
}

function quantile(sorted: number[], q: number): number {
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (base + 1 < sorted.length) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
}

interface BoxData {
  well_name: string;
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  count: number;
  color: string;
}

const MARGIN = { top: 12, right: 24, bottom: 70, left: 64 };
const CHART_HEIGHT = 440;

export function ConcentrationDistributionChart({ data, unit }: Props) {
  // Box plot compares wells side-by-side — default to showing all wells.
  // Analyte stays single (Benzene by default).
  const [hiddenWellIds, setHiddenWellIds] = useState<Set<string>>(new Set());
  const [selectedAnalyte, setSelectedAnalyte] = useState<string>(() => defaultSelectedAnalyteCode(data));

  const allAnalytes = useMemo(() => {
    const map = new Map<string, string>();
    for (const well of data.wells) {
      for (const [code, analyte] of Object.entries(well.analytes)) {
        if (!map.has(code)) map.set(code, analyte.compound);
      }
    }
    return Array.from(map.entries())
      .map(([code, compound]) => ({ code, compound }))
      .sort((a, b) => a.compound.toLowerCase().localeCompare(b.compound.toLowerCase()));
  }, [data.wells]);

  const activeAnalyte =
    selectedAnalyte ||
    (allAnalytes.find((a) => a.compound.toLowerCase() === 'benzene')?.code ?? allAnalytes[0]?.code ?? '');

  const visibleWells = data.wells.filter((w) => !hiddenWellIds.has(w.well_id));
  const visibleAnalyteCodes = useMemo(() => new Set([activeAnalyte].filter(Boolean)), [activeAnalyte]);
  const { lines: regulatoryLines } = useRegulatoryLines(data, visibleAnalyteCodes);

  const boxData: BoxData[] = useMemo(() => {
    return visibleWells
      .map((well): BoxData | null => {
        const analyte = well.analytes[activeAnalyte];
        const wellIdx = data.wells.findIndex((w) => w.well_id === well.well_id);
        const color: string = WELL_COLORS[wellIdx % WELL_COLORS.length];
        if (!analyte) return null;
        const values = analyte.measurements
          .map((m) => convertConc(m.concentration, unit))
          .filter((v): v is number => v !== null)
          .sort((a, b) => a - b);
        if (values.length === 0) return null;
        return {
          well_name: well.well_name,
          min: values[0],
          q1: quantile(values, 0.25),
          median: quantile(values, 0.5),
          q3: quantile(values, 0.75),
          max: values[values.length - 1],
          count: values.length,
          color,
        };
      })
      .filter((d: BoxData | null): d is BoxData => d !== null);
  }, [visibleWells, activeAnalyte, unit, data.wells]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(0, CHART_HEIGHT - MARGIN.top - MARGIN.bottom);

  const xScale = useMemo(() => {
    return scaleBand<string>()
      .domain(boxData.map((b) => b.well_name))
      .range([MARGIN.left, MARGIN.left + innerWidth])
      .padding(0.3);
  }, [boxData, innerWidth]);

  const yDomainMax = useMemo(() => {
    let yMax = 0;
    for (const b of boxData) if (b.max > yMax) yMax = b.max;
    for (const line of regulatoryLines) if (line.value > yMax) yMax = line.value;
    return yMax > 0 ? yMax * 1.1 : 1;
  }, [boxData, regulatoryLines]);

  const yScale = useMemo(
    () => scaleLinear().domain([0, yDomainMax]).range([MARGIN.top + innerHeight, MARGIN.top]).nice(),
    [yDomainMax, innerHeight],
  );

  const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

  const formatY = (v: number) => {
    if (v === 0) return '0';
    const abs = Math.abs(v);
    if (abs < 0.01) return v.toExponential(1);
    if (abs < 1) return v.toPrecision(2);
    return v.toFixed(1);
  };

  interface HoverState { box: BoxData; clientX: number; clientY: number }
  const [hover, setHover] = useState<HoverState | null>(null);

  const handleBoxHover = useCallback(
    (box: BoxData) => (e: React.MouseEvent) => {
      setHover({ box, clientX: e.clientX, clientY: e.clientY });
    },
    [],
  );
  const handleBoxLeave = useCallback(() => setHover(null), []);

  if (!data.wells.length) {
    return (
      <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
        NO ANALYTICAL DATA AVAILABLE
      </div>
    );
  }

  const wellItems = data.wells.map((w, i) => ({
    well_id: w.well_id,
    well_name: w.well_name,
    color: WELL_COLORS[i % WELL_COLORS.length],
  }));
  const visibleWellIdSet = new Set(visibleWells.map((w) => w.well_id));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-mono-500 tracking-wider">ANALYTE:</span>
        <div className="flex gap-1 flex-wrap">
          {allAnalytes.map((a) => (
            <button
              key={a.code}
              onClick={() => setSelectedAnalyte(a.code)}
              className={`px-2.5 py-1 text-[10px] tracking-wider rounded transition-colors ${
                activeAnalyte === a.code
                  ? 'bg-mono-700 text-mono-100'
                  : 'text-mono-500 hover:text-mono-300 hover:bg-mono-800'
              }`}
            >
              {a.compound}
            </button>
          ))}
        </div>
      </div>

      <WellSelectorPanel
        wells={wellItems}
        visibleWellIds={visibleWellIdSet}
        onToggle={(id) => {
          setHiddenWellIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          });
        }}
        onSelectAll={() => setHiddenWellIds(new Set())}
        onDeselectAll={() => setHiddenWellIds(new Set(data.wells.map((w) => w.well_id)))}
      />

      <div ref={containerRef}>
        {boxData.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
            NO DATA FOR SELECTED ANALYTE AND WELLS
          </div>
        ) : (
          <svg
            width={width || '100%'}
            height={CHART_HEIGHT}
            style={{ display: 'block', userSelect: 'none' }}
          >
            {/* Gridlines */}
            {yTicks.map((t) => {
              const y = yScale(t);
              return (
                <line
                  key={`grid-${t}`}
                  x1={MARGIN.left}
                  x2={MARGIN.left + innerWidth}
                  y1={y}
                  y2={y}
                  stroke="var(--dv-chart-grid)"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
              );
            })}

            {/* Regulatory lines */}
            {regulatoryLines.map((line) => {
              const v = convertConc(line.value, unit);
              if (v === null || v > yDomainMax) return null;
              const y = yScale(v);
              const dashArray = line.style === 'dashed' ? '8 4' : line.style === 'dotted' ? '2 4' : undefined;
              return (
                <g key={line.key} style={{ filter: 'url(#dv-mcl-glow)' }}>
                  <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={y} y2={y} stroke={line.color} strokeWidth={1} strokeDasharray={dashArray} />
                  <text x={MARGIN.left + innerWidth - 4} y={y - 4} textAnchor="end" fontSize={9} fill={line.color}>
                    {`${line.label} ${line.analyteName}`}
                  </text>
                </g>
              );
            })}

            {/* Box-and-whisker per well */}
            {boxData.map((b) => {
              const x = (xScale(b.well_name) ?? 0) + xScale.bandwidth() / 2;
              const boxWidth = Math.min(xScale.bandwidth(), 40);
              const xLeft = x - boxWidth / 2;
              const xRight = x + boxWidth / 2;
              const yMin = yScale(b.min);
              const yMax = yScale(b.max);
              const yQ1 = yScale(b.q1);
              const yQ3 = yScale(b.q3);
              const yMed = yScale(b.median);
              const isHovered = hover?.box === b;
              return (
                <g
                  key={`box-${b.well_name}`}
                  onMouseEnter={handleBoxHover(b)}
                  onMouseMove={handleBoxHover(b)}
                  onMouseLeave={handleBoxLeave}
                  style={{ cursor: 'crosshair' }}
                >
                  {/* Vertical whisker from min to max */}
                  <line x1={x} x2={x} y1={yMin} y2={yMax} stroke="var(--dv-text-muted)" strokeWidth={1} />
                  {/* Top whisker cap */}
                  <line x1={x - boxWidth / 4} x2={x + boxWidth / 4} y1={yMax} y2={yMax} stroke="var(--dv-text-muted)" strokeWidth={1} />
                  {/* Bottom whisker cap */}
                  <line x1={x - boxWidth / 4} x2={x + boxWidth / 4} y1={yMin} y2={yMin} stroke="var(--dv-text-muted)" strokeWidth={1} />
                  {/* Box (Q1 to Q3) */}
                  <rect
                    x={xLeft}
                    y={yQ3}
                    width={boxWidth}
                    height={Math.max(1, yQ1 - yQ3)}
                    fill={b.color}
                    fillOpacity={isHovered ? 0.45 : 0.25}
                    stroke={b.color}
                    strokeOpacity={0.9}
                    strokeWidth={1.5}
                    rx={2}
                  />
                  {/* Median line */}
                  <line x1={xLeft} x2={xRight} y1={yMed} y2={yMed} stroke="var(--dv-accent-cyan, #22d3ee)" strokeWidth={2} />
                </g>
              );
            })}

            {/* Y-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left} y1={MARGIN.top} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {yTicks.map((t) => {
              const y = yScale(t);
              return (
                <g key={`yt-${t}`}>
                  <line x1={MARGIN.left - 4} x2={MARGIN.left} y1={y} y2={y} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                  <text x={MARGIN.left - 8} y={y} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatY(t)}
                  </text>
                </g>
              );
            })}
            <text transform={`translate(${MARGIN.left - 48}, ${MARGIN.top + innerHeight / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)">
              Concentration (mg/L)
            </text>

            {/* X-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {boxData.map((b) => {
              const x = (xScale(b.well_name) ?? 0) + xScale.bandwidth() / 2;
              return (
                <text
                  key={`xt-${b.well_name}`}
                  transform={`translate(${x}, ${MARGIN.top + innerHeight + 12}) rotate(-35)`}
                  textAnchor="end"
                  fontSize={11}
                  fill="var(--dv-chart-axis)"
                >
                  {b.well_name}
                </text>
              );
            })}
          </svg>
        )}
      </div>

      {hover && (
        <ChartTooltip visible x={hover.clientX} y={hover.clientY} placement="top">
          <div style={{ minWidth: 180, fontSize: 11 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{hover.box.well_name}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 12, rowGap: 2, fontFamily: 'var(--dv-font-mono, ui-monospace, monospace)' }}>
              <span style={{ color: 'var(--dv-tooltip-unit, rgba(255,255,255,0.55))' }}>Max</span>
              <span>{hover.box.max.toFixed(3)}</span>
              <span style={{ color: 'var(--dv-tooltip-unit, rgba(255,255,255,0.55))' }}>Q3 (75%)</span>
              <span>{hover.box.q3.toFixed(3)}</span>
              <span style={{ color: 'var(--dv-accent-cyan, #22d3ee)' }}>Median</span>
              <span>{hover.box.median.toFixed(3)}</span>
              <span style={{ color: 'var(--dv-tooltip-unit, rgba(255,255,255,0.55))' }}>Q1 (25%)</span>
              <span>{hover.box.q1.toFixed(3)}</span>
              <span style={{ color: 'var(--dv-tooltip-unit, rgba(255,255,255,0.55))' }}>Min</span>
              <span>{hover.box.min.toFixed(3)}</span>
              <span style={{ color: 'var(--dv-tooltip-unit, rgba(255,255,255,0.55))' }}>n</span>
              <span>{hover.box.count}</span>
            </div>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}
